// ══════════════════════════════════════════════════════════════
//  AnimationBar — Bottom-Bar für Präsentationen
//  Filmstreifen + Transport-Controls + Slide-Popovers
//  Szene anlegen/aktualisieren, Drag & Drop, Duplizieren
// ══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useSceneStore } from '../../store/scenes'
import { captureViewerThumbnailPair } from '../../lib/captureViewerThumbnail'
import {
  Play, Pause, Plus, Trash2, Settings, Save,
  RotateCw, Camera, Copy, RefreshCw, Maximize2,
  Upload, Move, Image as ImageIcon, HelpCircle, Type,
  ChevronDown, X,
} from 'lucide-react'

// ── Types ──────────────────────────────────────────────────────

interface OverlayImage {
  id: string
  url: string
  fileName: string
  position: { x: number; y: number; z: number }
  rotation: { x: number; y: number; z: number }
  scale: { x: number; y: number }
  opacity: number
}

interface TextOverlay {
  id: string
  text: string
  fontSize: number    // in vw
  position: { x: number; y: number }  // in % (0–100)
  color: string       // hex
  opacity: number
}

interface AnimSlide {
  sceneId: string
  duration: number
  hold: number
  transition: 'fly' | 'orbit' | 'cut'
  orbitCenter: { x: number; y: number; z: number } | null
  orbitSpeed: number | null
  orbitEndAngle: number | null
  interpolateProperties: boolean
  holdTurntable: boolean
  holdTurntableRpm: number
  _sceneName?: string
  // ── Bild-Overlay ──
  overlayImages?: OverlayImage[]
  overlayTransition?: 'fade' | 'cut'
  overlaySpan?: number  // 1 = nur dieser Slide, 2 = über 2 Slides, 3 = über 3 Slides
  // ── Text-Overlay ──
  textOverlays?: TextOverlay[]
  textTransition?: 'fade' | 'cut'
  textSpan?: number
}

interface Presentation {
  id: string
  projectId: string
  name: string
  slides: AnimSlide[]
  createdAt: string
  updatedAt: string
}

interface AnimationMeta {
  id: string
  name: string
  description: string
  slides: AnimSlide[] | null
  createdAt: string
  updatedAt: string
}

// ── Helpers ────────────────────────────────────────────────────

/** Clamp a popup/popover position so it stays fully within the viewport.
 *  `anchor` = where the user clicked or the trigger element center.
 *  `mode`:
 *    - 'above'  → popup opens above anchor, horizontally centered (popovers)
 *    - 'cursor' → popup opens at cursor like a context menu (top-left = anchor)
 *  `est` = estimated popup size (used before the DOM element exists). */
function clampPopupPos(
  anchor: { x: number; y: number },
  mode: 'above' | 'cursor',
  est: { w: number; h: number } = { w: 200, h: 180 },
): React.CSSProperties {
  const pad = 8
  const vw = window.innerWidth
  const vh = window.innerHeight

  if (mode === 'cursor') {
    // Context-menu style: top-left at anchor, flip if needed
    let x = anchor.x
    let y = anchor.y
    if (x + est.w + pad > vw) x = vw - est.w - pad
    if (x < pad) x = pad
    if (y + est.h + pad > vh) y = anchor.y - est.h
    if (y < pad) y = pad
    return { left: x, top: y }
  }

  // 'above': popover centered above anchor
  let x = anchor.x
  let y = anchor.y
  // Horizontal: keep centered but clamp to viewport
  const halfW = est.w / 2
  if (x - halfW < pad) x = halfW + pad
  if (x + halfW > vw - pad) x = vw - halfW - pad
  // Vertical: prefer above, fall back to below if no room
  if (y - est.h - pad < 0) {
    // Not enough room above → open below
    return { left: x, top: y + pad, transform: 'translateX(-50%)' }
  }
  return { left: x, top: y, transform: 'translate(-50%, -100%) translateY(-8px)' }
}

// ── Overlay-Bild-Helpers ──────────────────────────────────────

function getViewer() { return (window as any).viewer }

let _ovlIdCounter = 0
function ovlUid() { return `ovl_${Date.now()}_${++_ovlIdCounter}` }

/** File → orientierungskorrigierte base64 Data-URL (skaliert auf max 2048px) */
function fileToDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const blobUrl = URL.createObjectURL(file)
    const img = new window.Image()
    img.onload = () => {
      const maxDim = 2048
      let w = img.naturalWidth, h = img.naturalHeight
      if (w > maxDim || h > maxDim) {
        const s = maxDim / Math.max(w, h)
        w = Math.round(w * s); h = Math.round(h * s)
      }
      const cvs = document.createElement('canvas')
      cvs.width = w; cvs.height = h
      cvs.getContext('2d')!.drawImage(img, 0, 0, w, h)
      URL.revokeObjectURL(blobUrl)
      resolve(cvs.toDataURL('image/jpeg', 0.85))
    }
    img.onerror = () => { URL.revokeObjectURL(blobUrl); reject(new Error('Bild laden fehlgeschlagen')) }
    img.src = blobUrl
  })
}

/** Smarte Default-Werte: Position am Orbit-Zentrum, zur Kamera gedreht */
function getOverlayDefaults(): {
  position: { x: number; y: number; z: number }
  rotation: { x: number; y: number; z: number }
  scale: { x: number; y: number }
} {
  const v = getViewer()
  if (!v?.scene?.view) return { position: { x: 0, y: 0, z: 0 }, rotation: { x: 0, y: 0, z: 0 }, scale: { x: 5, y: 4 } }
  const view = v.scene.view
  const pivot = view.getPivot?.() ?? view.position
  const camPos = view.position
  const dx = pivot.x - camPos.x, dy = pivot.y - camPos.y, dz = pivot.z - camPos.z
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
  const yRotDeg = Math.round(Math.atan2(-dx, -dz) * 180 / Math.PI)
  const scaleW = Math.max(2, Math.round(dist * 0.3 * 10) / 10)
  const r = (n: number) => Math.round(n * 100) / 100
  return {
    position: { x: r(pivot.x), y: r(pivot.y), z: r(pivot.z) },
    rotation: { x: 0, y: yRotDeg, z: 0 },
    scale: { x: scaleW, y: Math.max(1.5, Math.round(scaleW * 0.75 * 10) / 10) },
  }
}

// ── IndexedDB für Overlay-Bilder ──────────────────────────────

const OVL_DB_NAME = 'orto_overlay_images'
const OVL_DB_VERSION = 1
const OVL_STORE = 'images'

function ovlDbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(OVL_DB_NAME, OVL_DB_VERSION)
    req.onupgradeneeded = () => { req.result.createObjectStore(OVL_STORE) }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function ovlDbPut(id: string, dataUrl: string): Promise<void> {
  const db = await ovlDbOpen()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OVL_STORE, 'readwrite')
    tx.objectStore(OVL_STORE).put(dataUrl, id)
    tx.oncomplete = () => { db.close(); resolve() }
    tx.onerror = () => { db.close(); reject(tx.error) }
  })
}

async function ovlDbGet(id: string): Promise<string | undefined> {
  const db = await ovlDbOpen()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OVL_STORE, 'readonly')
    const req = tx.objectStore(OVL_STORE).get(id)
    req.onsuccess = () => { db.close(); resolve(req.result as string | undefined) }
    req.onerror = () => { db.close(); reject(req.error) }
  })
}

async function ovlDbDelete(id: string): Promise<void> {
  const db = await ovlDbOpen()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OVL_STORE, 'readwrite')
    tx.objectStore(OVL_STORE).delete(id)
    tx.oncomplete = () => { db.close(); resolve() }
    tx.onerror = () => { db.close(); reject(tx.error) }
  })
}

// ── AnimationBar ──────────────────────────────────────────────

export function AnimationBar() {
  const scenes = useSceneStore(s => s.scenes)
  const createScene = useSceneStore(s => s.createScene)
  const updateSceneStore = useSceneStore(s => s.updateScene)
  const [active, setActive] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [phase, setPhase] = useState('idle')
  const [slides, setSlides] = useState<AnimSlide[]>([])
  const [editingSlide, setEditingSlide] = useState<number | null>(null)
  const [popoverPos, setPopoverPos] = useState<{ x: number; y: number } | null>(null)
  const [ctxMenu, setCtxMenu] = useState<{ idx: number; x: number; y: number } | null>(null)
  const [presentationId, setPresentationId] = useState<string | null>(null)
  const [presentationName, setPresentationName] = useState('Präsentation')
  const [presentations, setPresentations] = useState<Presentation[]>([])
  const [showPicker, setShowPicker] = useState(false)
  const [pickerPos, setPickerPos] = useState<{ x: number; y: number } | null>(null)
  const addBtnRef = useRef<HTMLButtonElement>(null)
  const [presentationMode, setPresentationMode] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // ── Multi-Animation ──
  const [animations, setAnimations] = useState<AnimationMeta[]>([])
  const [activeAnimationId, setActiveAnimationId] = useState<string | null>(null)
  const [animDropdownOpen, setAnimDropdownOpen] = useState(false)
  const animDropdownRef = useRef<HTMLDivElement>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Turntable ──
  const [turntableActive, setTurntableActive] = useState(false)
  const [turntableSpeed, setTurntableSpeed] = useState(4)

  // ── Drag & Drop ──
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [dropIdx, setDropIdx] = useState<number | null>(null)

  // ── Bild-Drag Modus-Anzeige ──
  const [dragModeLabel, setDragModeLabel] = useState<string | null>(null)

  // ── Overlay-Bilder ──
  const [overlayEditIdx, setOverlayEditIdx] = useState<number | null>(null)
  const [overlayPopPos, setOverlayPopPos] = useState<{ x: number; y: number } | null>(null)
  const overlayFileRef = useRef<HTMLInputElement>(null)
  const overlayUploadTarget = useRef<number>(0)
  const [showHelp, setShowHelp] = useState(false)

  // ── Text-Overlay ──
  const [txtEditIdx, setTxtEditIdx] = useState<number | null>(null)
  const [txtPopPos, setTxtPopPos] = useState<{ x: number; y: number } | null>(null)

  // ── Drag & Drop (overlay slots) ──
  const [ovlDragIdx, setOvlDragIdx] = useState<number | null>(null)
  const [ovlDropIdx, setOvlDropIdx] = useState<number | null>(null)

  // ── Drag & Drop (text slots) ──
  const [txtDragIdx, setTxtDragIdx] = useState<number | null>(null)
  const [txtDropIdx, setTxtDropIdx] = useState<number | null>(null)

  const projectId = useMemo(() => {
    const store = (window as any).__authStore
    return store?.getState?.()?.currentProjectId || null
  }, [])

  // ── API fetch helper for animations-data ──
  const apiFetch = useCallback(async (path: string, opts?: RequestInit) => {
    if (!projectId) { console.warn('[AnimBar] apiFetch: no projectId'); return null }
    const fetchFn = (window as any)._apiFetch
    if (!fetchFn) {
      console.warn('[AnimBar] apiFetch: window._apiFetch not available, using direct fetch')
      // Fallback: direct fetch with token
      const token = localStorage.getItem('accessToken')
      const apiBase = (window as any)._expressApiBase || ''
      const url = `${apiBase}/api/projects/${encodeURIComponent(projectId)}/animations-data${path}`
      console.log(`[AnimBar] Fallback fetch: ${opts?.method || 'GET'} ${url}`)
      try {
        const res = await fetch(url, {
          ...opts,
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts?.headers as any || {}) },
        })
        if (!res.ok) { console.warn(`[AnimBar] HTTP ${res.status}`); return null }
        return res.json()
      } catch (e) { console.warn('[AnimBar] fetch error:', e); return null }
    }
    const url = `/api/projects/${encodeURIComponent(projectId)}/animations-data${path}`
    console.log(`[AnimBar] apiFetch: ${opts?.method || 'GET'} ${url}`)
    try {
      const res = await fetchFn(url, opts)
      if (!res.ok) { console.warn(`[AnimBar] HTTP ${res.status}`); return null }
      return res.json()
    } catch (e) { console.warn('[AnimBar] apiFetch error:', e); return null }
  }, [projectId])

  const loadAnimations = useCallback(async () => {
    const data = await apiFetch('/')
    if (data?.data && Array.isArray(data.data)) {
      setAnimations(data.data)
      return data.data as AnimationMeta[]
    }
    return []
  }, [apiFetch])

  // Close animation dropdown on outside click
  useEffect(() => {
    if (!animDropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (animDropdownRef.current && !animDropdownRef.current.contains(e.target as Node)) {
        setAnimDropdownOpen(false)
      }
    }
    document.addEventListener('pointerdown', handler)
    return () => document.removeEventListener('pointerdown', handler)
  }, [animDropdownOpen])

  // Auto-scroll filmstrip to active slide
  useEffect(() => {
    if (!scrollRef.current || slides.length === 0) return
    const container = scrollRef.current
    const slideEl = container.querySelectorAll('.anim-bar__slide')[currentSlide] as HTMLElement
    if (slideEl) {
      slideEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }, [currentSlide, slides.length])


  // ── Listen to engine events ─────────────────────────────────
  useEffect(() => {
    const onActivated = () => setActive(true)
    const onDeactivated = () => { setActive(false); setPlaying(false) }
    const onStateChange = (e: Event) => setPlaying((e as CustomEvent).detail.playing)
    const onSlideChange = (e: Event) => setCurrentSlide((e as CustomEvent).detail.slide)
    const onTick = (e: Event) => {
      const d = (e as CustomEvent).detail
      setPhase(d.phase)
      setCurrentSlide(d.slide)
    }
    const onLoaded = (e: Event) => {
      const d = (e as CustomEvent).detail
      setSlides(d.slides || [])
      setPresentationId(d.id)
      setPresentationName(d.name)
    }
    const onEnd = () => setPlaying(false)
    const onTurntable = (e: Event) => {
      const d = (e as CustomEvent).detail
      setTurntableActive(d.active)
      setTurntableSpeed(d.speed)
    }

    window.addEventListener('orto:animActivated', onActivated)
    window.addEventListener('orto:animDeactivated', onDeactivated)
    window.addEventListener('orto:animStateChange', onStateChange)
    window.addEventListener('orto:animSlideChange', onSlideChange)
    window.addEventListener('orto:animTick', onTick)
    window.addEventListener('orto:animLoaded', onLoaded)
    window.addEventListener('orto:animEnd', onEnd)
    window.addEventListener('orto:turntableChanged', onTurntable)

    // Bild-Drag Modus-Anzeige
    const onDragMode = (e: Event) => {
      const d = (e as CustomEvent).detail
      setDragModeLabel(d?.label || null)
    }
    window.addEventListener('orto:imgDragMode', onDragMode)

    const engine = (window as any)._animEngine
    if (engine?.active) { setActive(true); setSlides(engine.slides || []) }

    return () => {
      window.removeEventListener('orto:animActivated', onActivated)
      window.removeEventListener('orto:animDeactivated', onDeactivated)
      window.removeEventListener('orto:animStateChange', onStateChange)
      window.removeEventListener('orto:animSlideChange', onSlideChange)
      window.removeEventListener('orto:animTick', onTick)
      window.removeEventListener('orto:animLoaded', onLoaded)
      window.removeEventListener('orto:animEnd', onEnd)
      window.removeEventListener('orto:turntableChanged', onTurntable)
      window.removeEventListener('orto:imgDragMode', onDragMode)
    }
  }, [])

  // ── Load animations from API ─────────────────────────────
  useEffect(() => {
    if (!active || !projectId) return
    loadAnimations()
    // Also load legacy presentations for fallback
    const token = localStorage.getItem('accessToken')
    if (!token) return
    const apiBase = (window as any)._expressApiBase || ''
    fetch(`${apiBase}/api/projects/${encodeURIComponent(projectId)}/presentations`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json()).then(data => {
      if (Array.isArray(data)) setPresentations(data)
    }).catch(() => {})
  }, [active, projectId, loadAnimations])

  // ── Overlay Persist Key (mit Fallback) ──
  const overlayPersistKey = `orto_ovl_slides_${projectId || 'default'}`

  // ── Sync slides to engine ───────────────────────────────────
  const syncEngine = useCallback((newSlides: AnimSlide[]) => {
    setSlides(newSlides)
    const engine = (window as any)._animEngine
    if (engine) engine.loadPresentation({ id: presentationId, name: presentationName, slides: newSlides })
    // Auto-persist: Overlay-Metadaten in localStorage (ohne base64)
    try {
      const meta = newSlides.map(s => ({
        sceneId: s.sceneId,
        overlayImages: (s.overlayImages || []).map(img => ({
          id: img.id, fileName: img.fileName,
          position: img.position, rotation: img.rotation,
          scale: img.scale, opacity: img.opacity,
        })),
        overlayTransition: s.overlayTransition,
        overlaySpan: s.overlaySpan,
        textOverlays: s.textOverlays || [],
        textTransition: s.textTransition,
        textSpan: s.textSpan,
      }))
      localStorage.setItem(overlayPersistKey, JSON.stringify(meta))
      const imgCount = meta.reduce((n, s) => n + s.overlayImages.length, 0)
      if (imgCount > 0) console.log(`[OVL] Auto-persist: ${imgCount} Bilder-Metadaten gespeichert (key: ${overlayPersistKey})`)
    } catch (err) { console.warn('[OVL] localStorage persist error:', err) }
    // Bild-Daten in IndexedDB sichern
    for (const s of newSlides) {
      for (const img of (s.overlayImages || [])) {
        if (img.url?.startsWith('data:')) {
          ovlDbPut(img.id, img.url).catch(err => console.warn('[OVL] IndexedDB put error:', err))
        }
      }
    }
  }, [presentationId, presentationName, overlayPersistKey])

  // ── Animation CRUD (depends on syncEngine) ──
  const loadAnimation = useCallback(async (id: string) => {
    const data = await apiFetch(`/${id}`)
    if (!data?.data) return
    const anim = data.data as AnimationMeta
    setActiveAnimationId(anim.id)
    setPresentationName(anim.name)
    const rawSlides = (anim.slides || []) as AnimSlide[]
    const enriched = await Promise.all(rawSlides.map(async (s: AnimSlide) => {
      const scene = scenes.find(sc => sc.id === s.sceneId)
      const restoredImages = await Promise.all(
        (s.overlayImages || []).map(async (img: any) => {
          if (img.url) return img
          const dataUrl = await ovlDbGet(img.id).catch(() => undefined)
          return dataUrl ? { ...img, url: dataUrl } : null
        })
      )
      return {
        ...s,
        _sceneName: scene?.title || s._sceneName || '',
        overlayImages: restoredImages.filter(Boolean) as OverlayImage[],
      }
    }))
    syncEngine(enriched)
  }, [apiFetch, scenes, syncEngine])

  const saveAnimationToApi = useCallback(async () => {
    if (!projectId) return false
    const cleanSlides = slides.map(s => ({
      ...s,
      overlayImages: (s.overlayImages || []).map(({ url, ...rest }: any) => rest),
    }))

    try {
      if (activeAnimationId) {
        // Update existing
        const res = await apiFetch(`/${activeAnimationId}`, {
          method: 'PATCH',
          body: JSON.stringify({ name: presentationName, slides: cleanSlides }),
        })
        if (res) {
          if (typeof (window as any).showToast === 'function') (window as any).showToast('Animation gespeichert ✓', 'success')
          return true
        }
      } else {
        // Create new
        const res = await apiFetch('/', {
          method: 'POST',
          body: JSON.stringify({ name: presentationName || 'Animation', slides: cleanSlides }),
        })
        if (res?.data) {
          setActiveAnimationId(res.data.id)
          setAnimations(prev => [res.data, ...prev])
          if (typeof (window as any).showToast === 'function') (window as any).showToast('Animation erstellt ✓', 'success')
          return true
        }
      }
    } catch (e) {
      console.warn('[Anim] Speichern fehlgeschlagen:', e)
    }
    if (typeof (window as any).showToast === 'function') (window as any).showToast('Animation speichern fehlgeschlagen', 'error')
    return false
  }, [projectId, activeAnimationId, presentationName, slides, apiFetch])

  const saveAnimationDebounced = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => { saveAnimationToApi() }, 800)
  }, [saveAnimationToApi])

  const createAnimation = useCallback(async (name: string) => {
    const data = await apiFetch('/', {
      method: 'POST',
      body: JSON.stringify({ name, slides: [] }),
    })
    if (!data?.data) return
    const newAnim = data.data as AnimationMeta
    setAnimations(prev => [newAnim, ...prev])
    setActiveAnimationId(newAnim.id)
    setPresentationName(newAnim.name)
    syncEngine([])
    setAnimDropdownOpen(false)
  }, [apiFetch, syncEngine])

  const deleteAnimation = useCallback(async (id: string) => {
    if (!confirm('Animation wirklich loschen?')) return
    await apiFetch(`/${id}`, { method: 'DELETE' })
    const remaining = animations.filter(a => a.id !== id)
    setAnimations(remaining)
    if (activeAnimationId === id) {
      if (remaining.length > 0) {
        loadAnimation(remaining[0].id)
      } else {
        setActiveAnimationId(null)
        setPresentationName('Animation')
        syncEngine([])
      }
    }
    setAnimDropdownOpen(false)
  }, [apiFetch, animations, activeAnimationId, loadAnimation, syncEngine])

  // ── Build slides from scenes (quick-start) — mit Overlay-Restore ──
  const buildFromScenes = useCallback(async () => {
    const newSlides: AnimSlide[] = scenes.map(s => ({
      sceneId: s.id, duration: 2.0, hold: 3.0, transition: 'fly' as const,
      orbitCenter: null, orbitSpeed: null, orbitEndAngle: null,
      interpolateProperties: true, holdTurntable: false, holdTurntableRpm: 4, _sceneName: s.title,
    }))
    // Gespeicherte Overlay-Daten aus localStorage + IndexedDB wiederherstellen
    try {
      const raw = localStorage.getItem(overlayPersistKey)
      console.log(`[OVL] Restore: key="${overlayPersistKey}", found=${!!raw}`)
      if (raw) {
        const saved = JSON.parse(raw) as Array<{ sceneId: string; overlayImages: any[]; overlayTransition?: string; overlaySpan?: number; textOverlays?: any[]; textTransition?: string; textSpan?: number }>
        console.log(`[OVL] Saved overlay data: ${saved.length} entries, images: ${saved.reduce((n, s) => n + (s.overlayImages?.length || 0), 0)}, texts: ${saved.reduce((n, s) => n + (s.textOverlays?.length || 0), 0)}`)
        let restoredImgs = 0
        let restoredTxts = 0
        for (let si = 0; si < newSlides.length; si++) {
          const slide = newSlides[si]
          // Match by index first, fallback to sceneId
          const match = si < saved.length && saved[si].sceneId === slide.sceneId
            ? saved[si]
            : saved.find(s => s.sceneId === slide.sceneId)
          if (!match) continue

          // Bilder wiederherstellen
          if (match.overlayImages?.length) {
            const restored: OverlayImage[] = []
            for (const meta of match.overlayImages) {
              try {
                const dataUrl = await ovlDbGet(meta.id)
                if (dataUrl) {
                  restored.push({ ...meta, url: dataUrl })
                  restoredImgs++
                } else {
                  console.warn(`[OVL] Bild nicht in IndexedDB: ${meta.id} (${meta.fileName})`)
                }
              } catch (err) { console.warn('[OVL] IndexedDB get error:', err) }
            }
            if (restored.length > 0) {
              slide.overlayImages = restored
              slide.overlayTransition = match.overlayTransition as any
              slide.overlaySpan = match.overlaySpan
            }
          }

          // Text-Overlay wiederherstellen
          if (match.textOverlays?.length) {
            slide.textOverlays = match.textOverlays
            slide.textTransition = match.textTransition as any
            slide.textSpan = match.textSpan
            restoredTxts += match.textOverlays.length
          }
        }
        console.log(`[OVL] Restore: ${restoredImgs} Bilder, ${restoredTxts} Texte wiederhergestellt`)
      }
    } catch (err) { console.warn('[OVL] Overlay-Restore fehlgeschlagen:', err) }
    syncEngine(newSlides)
  }, [scenes, syncEngine, overlayPersistKey])

  // ── Auto-load first animation (or fallback to legacy presentation) ───
  const didAutoLoad = useRef(false)
  useEffect(() => {
    if (!active || didAutoLoad.current) return
    if (slides.length > 0) return // bereits Slides vorhanden
    // Prefer animations-data API
    if (animations.length > 0) {
      didAutoLoad.current = true
      loadAnimation(animations[0].id)
      return
    }
    // Fallback to legacy presentations
    if (presentations.length === 0) return
    didAutoLoad.current = true
    const pres = presentations[0]
    ;(async () => {
      const enriched = await Promise.all(pres.slides.map(async (s: any) => {
        const scene = scenes.find(sc => sc.id === s.sceneId)
        const restoredImages = await Promise.all(
          (s.overlayImages || []).map(async (img: any) => {
            if (img.url) return img
            const dataUrl = await ovlDbGet(img.id).catch(() => undefined)
            return dataUrl ? { ...img, url: dataUrl } : null
          })
        )
        return {
          ...s,
          _sceneName: scene?.title || s._sceneName || '',
          overlayImages: restoredImages.filter(Boolean) as OverlayImage[],
        }
      }))
      setPresentationId(pres.id)
      setPresentationName(pres.name)
      syncEngine(enriched)
    })()
  }, [active, animations, presentations, scenes, slides.length, syncEngine, loadAnimation])

  // ── Load a saved presentation ───────────────────────────────
  const loadPresentation = useCallback(async (pres: Presentation) => {
    // Restore overlay image data-URLs from IndexedDB
    const enriched = await Promise.all(pres.slides.map(async s => {
      const scene = scenes.find(sc => sc.id === s.sceneId)
      const restoredImages = await Promise.all(
        (s.overlayImages || []).map(async img => {
          if (img.url) return img // already has data-URL (in-memory)
          const dataUrl = await ovlDbGet(img.id).catch(() => undefined)
          return dataUrl ? { ...img, url: dataUrl } : null
        })
      )
      return {
        ...s,
        _sceneName: scene?.title || s._sceneName || '',
        overlayImages: restoredImages.filter(Boolean) as OverlayImage[],
      }
    }))
    setPresentationId(pres.id)
    setPresentationName(pres.name)
    setShowPicker(false)
    syncEngine(enriched)
  }, [scenes, syncEngine])

  // ── Save to API (animations-data) ──
  const savePresentation = useCallback(async () => {
    if (!projectId) return
    const saved = await saveAnimationToApi()
    if (saved) loadAnimations()
  }, [projectId, saveAnimationToApi, loadAnimations])

  // ── Slide CRUD ──────────────────────────────────────────────
  const addSceneSlide = useCallback((sceneId: string) => {
    const scene = scenes.find(s => s.id === sceneId)
    const newSlide: AnimSlide = {
      sceneId, duration: 2.0, hold: 3.0, transition: 'fly',
      orbitCenter: null, orbitSpeed: null, orbitEndAngle: null,
      interpolateProperties: true, holdTurntable: false, holdTurntableRpm: 4, _sceneName: scene?.title || '',
    }
    syncEngine([...slides, newSlide])
    setShowPicker(false)
  }, [scenes, slides, syncEngine])

  const removeSlide = useCallback((idx: number) => {
    syncEngine(slides.filter((_, i) => i !== idx))
    setEditingSlide(null)
  }, [slides, syncEngine])

  const updateSlide = useCallback((idx: number, patch: Partial<AnimSlide>) => {
    syncEngine(slides.map((s, i) => i === idx ? { ...s, ...patch } : s))
  }, [slides, syncEngine])

  const duplicateSlide = useCallback((idx: number) => {
    const orig = slides[idx]
    if (!orig) return
    // Szene aus dem Store holen und als neue Szene duplizieren
    const origScene = scenes.find(s => s.id === orig.sceneId)
    const vs = origScene?.viewerState || (window as any).captureFullViewerState?.()
    const title = `${orig._sceneName || 'Szene'} (Kopie)`
    const thumbs = origScene
      ? { small: origScene.thumbnailSmall, medium: origScene.thumbnailMedium }
      : captureViewerThumbnailPair()
    const newScene = createScene({
      title,
      viewerState: vs,
      projectId: projectId || undefined,
      activeCloudIds: vs?.clouds?.filter((c: any) => c.visible).map((c: any) => c.id) || [],
      thumbnailSmall: thumbs?.small || undefined,
      thumbnailMedium: thumbs?.medium || undefined,
    })
    const copy: AnimSlide = {
      ...orig,
      sceneId: newScene.id,
      _sceneName: newScene.title,
      overlayImages: (orig.overlayImages || []).map(img => ({ ...img })),
    }
    const updated = [...slides]
    updated.splice(idx + 1, 0, copy)
    syncEngine(updated)
  }, [slides, scenes, createScene, projectId, syncEngine])

  // ── Overlay-Bilder CRUD ─────────────────────────────────────
  const triggerOverlayUpload = useCallback((slideIdx: number) => {
    console.log('[OVL] triggerOverlayUpload slideIdx=', slideIdx, 'inputRef=', !!overlayFileRef.current)
    overlayUploadTarget.current = slideIdx
    overlayFileRef.current?.click()
  }, [])

  const handleOverlayFiles = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[OVL] handleOverlayFiles fired, files=', e.target.files?.length, 'target=', overlayUploadTarget.current, 'slides.length=', slides.length)
    const files = e.target.files
    if (!files || files.length === 0) { console.warn('[OVL] no files'); return }
    const slideIdx = overlayUploadTarget.current
    if (slideIdx == null || slideIdx < 0 || slideIdx >= slides.length) {
      console.warn('[OVL] invalid slideIdx', slideIdx, 'slides.length=', slides.length)
      return
    }
    const defaults = getOverlayDefaults()
    const existing = slides[slideIdx]?.overlayImages || []
    console.log('[OVL] processing', files.length, 'files, existing=', existing.length)

    const newImages: OverlayImage[] = []
    const fileArr = Array.from(files).filter(f => f.type.startsWith('image/'))
    console.log('[OVL] image files after filter:', fileArr.length)

    // Rechts-Vektor → Bilder horizontal verteilen
    const yRotRad = (defaults.rotation.y || 0) * Math.PI / 180
    const rightX = Math.cos(yRotRad), rightZ = -Math.sin(yRotRad)
    const spacing = defaults.scale.x * 1.15
    const total = existing.length + fileArr.length

    for (let fi = 0; fi < fileArr.length; fi++) {
      const dataUrl = await fileToDataURL(fileArr[fi])
      const imgId = ovlUid()
      await ovlDbPut(imgId, dataUrl).catch(err => console.warn('[OVL] IndexedDB put error:', err))
      const idx = existing.length + fi
      const offset = (idx - (total - 1) / 2) * spacing
      newImages.push({
        id: imgId,
        url: dataUrl,
        fileName: fileArr[fi].name,
        position: {
          x: Math.round((defaults.position.x + rightX * offset) * 100) / 100,
          y: Math.round(defaults.position.y * 100) / 100,
          z: Math.round((defaults.position.z + rightZ * offset) * 100) / 100,
        },
        rotation: { ...defaults.rotation },
        scale: { ...defaults.scale },
        opacity: 1,
      })
    }

    console.log('[OVL] new images built:', newImages.length)
    if (newImages.length > 0) {
      updateSlide(slideIdx, { overlayImages: [...existing, ...newImages] })
      console.log('[OVL] updateSlide called, total now:', existing.length + newImages.length)
    }
    e.target.value = ''
  }, [slides, updateSlide])

  const removeOverlayImage = useCallback((slideIdx: number, imgId: string) => {
    const images = slides[slideIdx]?.overlayImages || []
    updateSlide(slideIdx, { overlayImages: images.filter(im => im.id !== imgId) })
    ovlDbDelete(imgId).catch(() => {})
  }, [slides, updateSlide])

  const updateOverlayImage = useCallback((slideIdx: number, imgId: string, patch: Partial<OverlayImage>) => {
    const images = slides[slideIdx]?.overlayImages || []
    updateSlide(slideIdx, { overlayImages: images.map(im => im.id === imgId ? { ...im, ...patch } : im) })
  }, [slides, updateSlide])

  const openOverlayPopover = useCallback((e: React.MouseEvent, slideIdx: number) => {
    e.stopPropagation()
    if (overlayEditIdx === slideIdx) { setOverlayEditIdx(null); setOverlayPopPos(null); return }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setOverlayPopPos({ x: rect.left + rect.width / 2, y: rect.top })
    setOverlayEditIdx(slideIdx)
  }, [overlayEditIdx])

  // Outside click closes overlay popover
  useEffect(() => {
    if (overlayEditIdx === null) return
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (t.closest('.ovl-popover--portal') || t.closest('.ovl-slot')) return
      setOverlayEditIdx(null); setOverlayPopPos(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [overlayEditIdx])

  // ── Text-Overlay CRUD ───────────────────────────────────────
  let _txtIdCounter = useRef(0)
  const txtUid = useCallback(() => `txt_${Date.now()}_${++_txtIdCounter.current}`, [])

  const addTextOverlay = useCallback((slideIdx: number) => {
    const existing = slides[slideIdx]?.textOverlays || []
    const newTxt: TextOverlay = {
      id: txtUid(),
      text: '',
      fontSize: 3,
      position: { x: 50, y: 50 },
      color: '#ffffff',
      opacity: 1,
    }
    updateSlide(slideIdx, { textOverlays: [...existing, newTxt] })
  }, [slides, updateSlide, txtUid])

  const removeTextOverlay = useCallback((slideIdx: number, txtId: string) => {
    const texts = slides[slideIdx]?.textOverlays || []
    updateSlide(slideIdx, { textOverlays: texts.filter(t => t.id !== txtId) })
  }, [slides, updateSlide])

  const updateTextOverlay = useCallback((slideIdx: number, txtId: string, patch: Partial<TextOverlay>) => {
    const texts = slides[slideIdx]?.textOverlays || []
    updateSlide(slideIdx, { textOverlays: texts.map(t => t.id === txtId ? { ...t, ...patch } : t) })
  }, [slides, updateSlide])

  const openTxtPopover = useCallback((e: React.MouseEvent, slideIdx: number) => {
    e.stopPropagation()
    if (txtEditIdx === slideIdx) { setTxtEditIdx(null); setTxtPopPos(null); return }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setTxtPopPos({ x: rect.left + rect.width / 2, y: rect.top })
    setTxtEditIdx(slideIdx)
  }, [txtEditIdx])

  // Close text popover on outside click
  useEffect(() => {
    if (txtEditIdx === null) return
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (t.closest('.txt-popover--portal') || t.closest('.txt-slot')) return
      setTxtEditIdx(null); setTxtPopPos(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [txtEditIdx])

  // ── Text-Slot Drag & Drop ──
  const handleTxtDragStart = useCallback((e: React.DragEvent, idx: number) => {
    e.stopPropagation()
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', `txt:${idx}`)
    setTxtDragIdx(idx)
  }, [])

  const handleTxtDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
    setTxtDropIdx(idx)
  }, [])

  const handleTxtDrop = useCallback((e: React.DragEvent, targetIdx: number) => {
    e.preventDefault()
    e.stopPropagation()
    if (txtDragIdx === null || txtDragIdx === targetIdx) { setTxtDragIdx(null); setTxtDropIdx(null); return }
    const srcSlide = slides[txtDragIdx]
    const tgtSlide = slides[targetIdx]
    if (!srcSlide || !tgtSlide) { setTxtDragIdx(null); setTxtDropIdx(null); return }
    // Swap text overlay data
    const next = slides.map((s, i) => {
      if (i === txtDragIdx) return { ...s, textOverlays: tgtSlide.textOverlays || [], textTransition: tgtSlide.textTransition, textSpan: tgtSlide.textSpan }
      if (i === targetIdx) return { ...s, textOverlays: srcSlide.textOverlays || [], textTransition: srcSlide.textTransition, textSpan: srcSlide.textSpan }
      return s
    })
    syncEngine(next)
    setTxtDragIdx(null)
    setTxtDropIdx(null)
  }, [txtDragIdx, slides, syncEngine])

  const handleTxtDragEnd = useCallback(() => { setTxtDragIdx(null); setTxtDropIdx(null) }, [])

  // ── Sync Text-Overlay-Daten zur DOM-Engine ──
  useEffect(() => {
    const engine = (window as any)._txtOverlayEngine
    if (!engine) return
    const txtSlides = slides.map(s => ({
      texts: s.textOverlays || [],
      transition: s.textTransition || 'cut',
      span: s.textSpan || 1,
    }))
    engine.setSlides(txtSlides)
  }, [slides])

  // ── Sync overlay-Daten zur 3D-Engine wenn Slides sich ändern ──
  useEffect(() => {
    const engine = (window as any)._imgOverlayEngine
    if (!engine) return
    const imgSlides = slides.map(s => ({
      images: s.overlayImages || [],
      transition: s.overlayTransition || 'cut',
      span: s.overlaySpan || 1,
    }))
    engine.setSlides(imgSlides)
  }, [slides])

  // ── 3D-Drag-Änderungen zurück in den State übernehmen ──
  useEffect(() => {
    const onChanged = (e: Event) => {
      const d = (e as CustomEvent).detail
      if (!d?.slides) return
      const newSlides = slides.map((s, i) => ({
        ...s,
        overlayImages: d.slides[i]?.images || s.overlayImages || [],
      }))
      syncEngine(newSlides)
    }
    window.addEventListener('orto:imgOverlaySlidesChanged', onChanged)
    return () => window.removeEventListener('orto:imgOverlaySlidesChanged', onChanged)
  }, [slides, syncEngine])

  // Sync text overlay drag changes back from engine
  useEffect(() => {
    const onChanged = (e: Event) => {
      const d = (e as CustomEvent).detail
      if (!d.slides) return
      const newSlides = slides.map((s, i) => ({
        ...s,
        textOverlays: d.slides[i]?.texts || s.textOverlays || [],
      }))
      syncEngine(newSlides)
    }
    window.addEventListener('orto:txtOverlaySlidesChanged', onChanged)
    return () => window.removeEventListener('orto:txtOverlaySlidesChanged', onChanged)
  }, [slides, syncEngine])

  // ── Drag & Drop reorder ─────────────────────────────────────
  const handleDragStart = useCallback((e: React.DragEvent, idx: number) => {
    e.dataTransfer.effectAllowed = 'move'
    setDragIdx(idx)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDropIdx(idx)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent, targetIdx: number) => {
    e.preventDefault()
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); setDropIdx(null); return }
    const next = [...slides]
    const [moved] = next.splice(dragIdx, 1)
    next.splice(targetIdx, 0, moved)
    syncEngine(next)
    setDragIdx(null)
    setDropIdx(null)
  }, [dragIdx, slides, syncEngine])

  const handleDragEnd = useCallback(() => { setDragIdx(null); setDropIdx(null) }, [])

  // ── Overlay Drag & Drop (reassign images between slides) ──
  const handleOvlDragStart = useCallback((e: React.DragEvent, idx: number) => {
    e.stopPropagation()
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', `ovl:${idx}`)
    setOvlDragIdx(idx)
  }, [])

  const handleOvlDragOver = useCallback((e: React.DragEvent, idx: number) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
    setOvlDropIdx(idx)
  }, [])

  const handleOvlDrop = useCallback((e: React.DragEvent, targetIdx: number) => {
    e.preventDefault()
    e.stopPropagation()
    if (ovlDragIdx === null || ovlDragIdx === targetIdx) { setOvlDragIdx(null); setOvlDropIdx(null); return }
    const srcSlide = slides[ovlDragIdx]
    const tgtSlide = slides[targetIdx]
    if (!srcSlide || !tgtSlide) { setOvlDragIdx(null); setOvlDropIdx(null); return }
    const next = slides.map((s, i) => {
      if (i === ovlDragIdx) return { ...s, overlayImages: tgtSlide.overlayImages || [], overlayTransition: tgtSlide.overlayTransition, overlaySpan: tgtSlide.overlaySpan }
      if (i === targetIdx) return { ...s, overlayImages: srcSlide.overlayImages || [], overlayTransition: srcSlide.overlayTransition, overlaySpan: srcSlide.overlaySpan }
      return s
    })
    syncEngine(next)
    setOvlDragIdx(null)
    setOvlDropIdx(null)
  }, [ovlDragIdx, slides, syncEngine])

  const handleOvlDragEnd = useCallback(() => { setOvlDragIdx(null); setOvlDropIdx(null) }, [])

  // ── Popover positioning (portal-based to escape overflow clips) ──
  const openPopover = useCallback((e: React.MouseEvent, idx: number) => {
    e.stopPropagation()
    if (editingSlide === idx) { setEditingSlide(null); setPopoverPos(null); return }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    // Anchor above the entire bar, not just above the button
    const bar = (e.currentTarget as HTMLElement).closest('.anim-bar__bottom')
    const barTop = bar ? bar.getBoundingClientRect().top : rect.top
    setPopoverPos({ x: rect.left + rect.width / 2, y: barTop })
    setEditingSlide(idx)
  }, [editingSlide])

  // Close popover on outside click (check if click was inside a portal popover)
  const popoverRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (editingSlide === null) return
    const handler = (e: MouseEvent) => {
      // Check if click is inside a portal popover
      const target = e.target as HTMLElement
      if (target.closest('.anim-bar__popover--portal') || target.closest('.anim-bar__transition-popover--portal')) return
      setEditingSlide(null)
      setPopoverPos(null)
    }
    // Use setTimeout to avoid catching the opening click
    const timer = setTimeout(() => {
      window.addEventListener('pointerdown', handler)
    }, 0)
    return () => { clearTimeout(timer); window.removeEventListener('pointerdown', handler) }
  }, [editingSlide])

  // ── Context menu ──
  const openCtxMenu = useCallback((e: React.MouseEvent, idx: number) => {
    e.preventDefault()
    e.stopPropagation()
    setCtxMenu({ idx, x: e.clientX, y: e.clientY })
    // Close any open popover
    setEditingSlide(null)
    setPopoverPos(null)
  }, [])

  const closeCtxMenu = useCallback(() => setCtxMenu(null), [])

  useEffect(() => {
    if (!ctxMenu) return
    const handler = () => setCtxMenu(null)
    const timer = setTimeout(() => window.addEventListener('pointerdown', handler), 0)
    return () => { clearTimeout(timer); window.removeEventListener('pointerdown', handler) }
  }, [ctxMenu])

  const ctxUpdateScene = useCallback((idx: number) => {
    const slide = slides[idx]
    if (!slide) return
    const vs = (window as any).captureFullViewerState?.()
    if (!vs) return
    const thumbs = captureViewerThumbnailPair()
    updateSceneStore(slide.sceneId, {
      viewerState: vs,
      activeCloudIds: vs.clouds?.filter((c: any) => c.visible).map((c: any) => c.id) || [],
      updatedAt: new Date().toISOString(),
      ...(thumbs ? { thumbnailSmall: thumbs.small, thumbnailMedium: thumbs.medium } : {}),
    })
    setCtxMenu(null)
  }, [slides, updateSceneStore])

  // ── Scene actions ───────────────────────────────────────────
  const handleCreateScene = useCallback(() => {
    const vs = (window as any).captureFullViewerState?.()
    if (!vs) return
    const title = `Szene ${scenes.length + 1}`

    // Thumbnails vom aktuellen Viewer-Canvas capturen
    const thumbs = captureViewerThumbnailPair()

    const scene = createScene({
      title,
      viewerState: vs,
      projectId: projectId || undefined,
      activeCloudIds: vs.clouds?.filter((c: any) => c.visible).map((c: any) => c.id) || [],
      thumbnailSmall: thumbs?.small || undefined,
      thumbnailMedium: thumbs?.medium || undefined,
    })
    // Add as slide
    const newSlide: AnimSlide = {
      sceneId: scene.id, duration: 2.0, hold: 3.0, transition: 'fly',
      orbitCenter: null, orbitSpeed: null, orbitEndAngle: null,
      interpolateProperties: true, holdTurntable: false, holdTurntableRpm: 4, _sceneName: scene.title,
    }
    syncEngine([...slides, newSlide])
  }, [scenes, createScene, projectId, slides, syncEngine])

  const handleUpdateScene = useCallback(() => {
    const slide = slides[currentSlide]
    if (!slide) return
    const vs = (window as any).captureFullViewerState?.()
    if (!vs) return
    const thumbs = captureViewerThumbnailPair()
    updateSceneStore(slide.sceneId, {
      viewerState: vs,
      activeCloudIds: vs.clouds?.filter((c: any) => c.visible).map((c: any) => c.id) || [],
      updatedAt: new Date().toISOString(),
      ...(thumbs ? { thumbnailSmall: thumbs.small, thumbnailMedium: thumbs.medium } : {}),
    })
  }, [slides, currentSlide, updateSceneStore])


  // ── Transport ───────────────────────────────────────────────
  const engine = (window as any)._animEngine
  const handlePlay = useCallback(() => engine?.play(), [])
  const handlePause = useCallback(() => engine?.pause(), [])
  const handleGoTo = useCallback((idx: number) => engine?.goTo(idx), [])

  // ── Präsentationsmodus ─────────────────────────────────────
  const enterPresentationMode = useCallback(() => {
    setPresentationMode(true)
    // UI-Elemente verstecken
    document.querySelectorAll<HTMLElement>(
      '.ghost-content, .nav-rail, .building-float, .tab-bar, .react-root > div > header'
    ).forEach(el => { el.dataset.prePresentation = el.style.display; el.style.display = 'none' })
    document.documentElement.requestFullscreen?.()
  }, [])

  const exitPresentationMode = useCallback(() => {
    setPresentationMode(false)
    // UI-Elemente wiederherstellen
    document.querySelectorAll<HTMLElement>(
      '.ghost-content, .nav-rail, .building-float, .tab-bar, .react-root > div > header'
    ).forEach(el => {
      if (el.dataset.prePresentation !== undefined) {
        el.style.display = el.dataset.prePresentation
        delete el.dataset.prePresentation
      } else {
        el.style.display = ''
      }
    })
    if (document.fullscreenElement) document.exitFullscreen?.()
  }, [])

  // Esc-Override: Präsentationsmodus → Bar einblenden, dann nochmal → Animation beenden
  useEffect(() => {
    if (!active) return
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (presentationMode) {
        e.preventDefault()
        e.stopImmediatePropagation()
        exitPresentationMode()
      }
      // Wenn nicht im Präsentationsmodus, lässt der normale _animOnKeyDown Esc durch → deactivate
    }
    document.addEventListener('keydown', handler, { capture: true })
    return () => document.removeEventListener('keydown', handler, { capture: true })
  }, [active, presentationMode, exitPresentationMode])

  // Browser-Fullscreen verlassen (Esc im Fullscreen wird vom Browser geschluckt)
  // → fullscreenchange feuert stattdessen
  useEffect(() => {
    if (!active || !presentationMode) return
    const onFsChange = () => {
      if (!document.fullscreenElement) exitPresentationMode()
    }
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
  }, [active, presentationMode, exitPresentationMode])

  // Cleanup: wenn Animation deaktiviert wird, Präsentationsmodus beenden
  useEffect(() => {
    if (!active && presentationMode) exitPresentationMode()
  }, [active, presentationMode, exitPresentationMode])

  const getThumb = useCallback((sceneId: string) => {
    const scene = scenes.find(s => s.id === sceneId)
    return scene?.thumbnailSmall || scene?.thumbnailMedium || null
  }, [scenes])

  if (!active) return null

  // ── Render ──────────────────────────────────────────────────
  const mainBar = createPortal(
    <div className={`anim-bar${presentationMode ? ' anim-bar--presentation' : ''}`}>

      {/* ═══ Bottom Bar (hidden in presentation mode) ═══ */}
      <div className={`anim-bar__bottom${presentationMode ? ' anim-bar__bottom--hidden' : ''}`}>
        {/* Help / Tastenkürzel */}
        <div className="anim-bar__help-wrap">
          <button className="anim-bar__help-btn" onClick={() => setShowHelp(!showHelp)} title="Tastenkürzel">
            <HelpCircle size={16} />
          </button>
          {showHelp && (
            <div className="anim-bar__help-box" onPointerDown={e => e.stopPropagation()}>
              <div className="anim-bar__help-title">Bild-Overlay Steuerung</div>
              <div className="anim-bar__help-row"><kbd>X</kbd> + Drag <span>Verschieben X-Achse</span></div>
              <div className="anim-bar__help-row"><kbd>Y</kbd> + Drag <span>Verschieben Horizontal</span></div>
              <div className="anim-bar__help-row"><kbd>C</kbd> + Drag <span>Verschieben Höhe</span></div>
              <div className="anim-bar__help-row"><kbd>R</kbd> + Drag <span>Drehen</span></div>
              <div className="anim-bar__help-row"><kbd>⇧R</kbd> + Drag <span>Kippen</span></div>
              <div className="anim-bar__help-row"><kbd>V</kbd> + Drag <span>Skalierung</span></div>
            </div>
          )}
        </div>

        {/* Drag-Modus Indikator */}
        {dragModeLabel && (
          <div className="anim-bar__drag-mode">{dragModeLabel}</div>
        )}

        {/* Transport */}
        <div className="anim-bar__transport">
          <button className="anim-bar__btn anim-bar__btn--play" onClick={playing ? handlePause : handlePlay}
            data-tip={playing ? 'Pause' : 'Play'}>
            {playing ? <Pause size={18} /> : <Play size={18} />}
          </button>
          <button
            className={`anim-bar__btn anim-bar__btn--turntable${turntableActive ? ' anim-bar__btn--active' : ''}`}
            onClick={() => {
              const engine = (window as any)._animEngine
              engine?.turntableToggle(turntableSpeed)
            }}
            data-tip={turntableActive ? 'Turntable stoppen' : 'Turntable'}
          >
            <RotateCw size={16} className={turntableActive ? 'spin' : ''} />
          </button>
        </div>

        {/* Separator */}
        <div className="anim-bar__sep" />

        {/* Scene actions (speichern + hinzufügen) */}
        <div className="anim-bar__scene-btns">
          <button className="anim-bar__btn" onClick={handleCreateScene} data-tip="Szene speichern">
            <Camera size={16} />
          </button>
          <button ref={addBtnRef} className="anim-bar__btn" onClick={() => {
            if (showPicker) { setShowPicker(false); return }
            const r = addBtnRef.current?.getBoundingClientRect()
            const bar = addBtnRef.current?.closest('.anim-bar__bottom')
            const barTop = bar ? bar.getBoundingClientRect().top : r?.top ?? 0
            if (r) setPickerPos({ x: r.left + r.width / 2, y: barTop })
            setShowPicker(true)
          }} data-tip="Szene hinzufügen">
            <Plus size={16} />
          </button>
        </div>

        {/* Filmstrip */}
        <div className="anim-bar__filmstrip" ref={scrollRef}>
          {slides.length === 0 ? (
            <div className="anim-bar__empty">
              <button className="anim-bar__quick-start" onClick={buildFromScenes} data-tip="Alle gespeicherten Szenen als Slides laden">Alle Szenen laden</button>
            </div>
          ) : (
            slides.map((slide, i) => {
              const thumb = getThumb(slide.sceneId)
              const isCurrent = i === currentSlide
              return (
                <React.Fragment key={`${slide.sceneId}-${i}`}>
                  {/* ── Transition-Indikator zwischen Slides (read-only) ── */}
                  {i > 0 && (
                    <div
                      className="anim-bar__transition-dot"
                      data-tip={`${slide.duration}s ${slide.transition}`}
                    >
                      <span className="anim-bar__transition-dot__icon">
                        {slide.transition === 'orbit' ? '↻' : slide.transition === 'cut' ? '⚡' : '→'}
                      </span>
                      <span className="anim-bar__transition-dot__label">{slide.duration}s</span>
                    </div>
                  )}

                  {/* ── Slide-Spalte: Szene + Bild-Slot + Text-Slot ── */}
                  <div className="anim-bar__slide-col">
                    {/* Scene card */}
                    <div
                      className={`anim-bar__slide${isCurrent ? ' anim-bar__slide--active' : ''}${dropIdx === i && dragIdx !== null && dragIdx !== i ? ' anim-bar__slide--drop' : ''}`}
                      onClick={() => handleGoTo(i)}
                      onContextMenu={e => openCtxMenu(e, i)}
                      draggable
                      onDragStart={e => handleDragStart(e, i)}
                      onDragOver={e => handleDragOver(e, i)}
                      onDrop={e => handleDrop(e, i)}
                      onDragEnd={handleDragEnd}
                    >
                      {thumb ? (
                        <img className="anim-bar__slide-thumb" src={thumb} alt="" draggable={false} />
                      ) : (
                        <div className="anim-bar__slide-placeholder"><span>{i + 1}</span></div>
                      )}
                      <div className="anim-bar__slide-label">{slide._sceneName || `Szene ${i + 1}`}</div>

                      {/* Hold-Badge */}
                      <div className="anim-bar__slide-hold">
                        {slide.holdTurntable ? <RotateCw size={8} /> : <Pause size={8} />}
                        <span>{slide.hold}s</span>
                      </div>

                      {/* Settings */}
                      <button className="anim-bar__slide-settings"
                        onClick={e => { e.stopPropagation(); openPopover(e, i) }} title="Einstellungen">
                        <Settings size={10} />
                      </button>
                    </div>

                    {/* Overlay image slot */}
                    {(() => {
                      const ovlSpan = slide.overlaySpan ?? 1
                      const imgs = slide.overlayImages || []
                      const prevOvlCovers = slides.some((ps, pi) => pi < i && pi + (ps.overlaySpan || 1) > i)
                      if (prevOvlCovers) return null
                      return (
                        <div
                          className={`ovl-slot${isCurrent ? ' ovl-slot--active' : ''}${ovlSpan > 1 ? ` ovl-slot--span${ovlSpan}` : ''}${ovlDragIdx === i ? ' ovl-slot--dragging' : ''}${ovlDropIdx === i && ovlDragIdx !== null && ovlDragIdx !== i ? ' ovl-slot--drop-target' : ''}`}
                          onClick={e => openOverlayPopover(e, i)}
                          title="Bild-Overlay"
                          draggable={imgs.length > 0}
                          onDragStart={e => handleOvlDragStart(e, i)}
                          onDragOver={e => handleOvlDragOver(e, i)}
                          onDrop={e => handleOvlDrop(e, i)}
                          onDragEnd={handleOvlDragEnd}
                        >
                          {imgs[0] ? (
                            <img className="ovl-slot__thumb" src={imgs[0].url} alt="" draggable={false} />
                          ) : (
                            <div className="ovl-slot__empty"><ImageIcon size={10} /></div>
                          )}
                          {imgs.length > 1 && <span className="ovl-slot__count">{imgs.length}</span>}
                          {ovlSpan > 1 && <span className="ovl-slot__span-badge">{ovlSpan}×</span>}
                        </div>
                      )
                    })()}

                    {/* Text overlay slot */}
                    {(() => {
                      const txtSpan = slide.textSpan ?? 1
                      const txts = slide.textOverlays || []
                      const prevTxtCovers = slides.some((ps, pi) => pi < i && pi + (ps.textSpan || 1) > i)
                      if (prevTxtCovers) return null
                      const firstText = txts[0]?.text || ''
                      const previewText = firstText.length > 12 ? firstText.slice(0, 12) + '…' : firstText
                      return (
                        <div
                          className={`txt-slot${isCurrent ? ' txt-slot--active' : ''}${txtSpan > 1 ? ` txt-slot--span${txtSpan}` : ''}${txtDragIdx === i ? ' txt-slot--dragging' : ''}${txtDropIdx === i && txtDragIdx !== null && txtDragIdx !== i ? ' txt-slot--drop-target' : ''}`}
                          onClick={e => { if (txts.length === 0) { addTextOverlay(i); openTxtPopover(e, i) } else { openTxtPopover(e, i) } }}
                          title="Text-Overlay"
                          draggable={txts.length > 0}
                          onDragStart={e => handleTxtDragStart(e, i)}
                          onDragOver={e => handleTxtDragOver(e, i)}
                          onDrop={e => handleTxtDrop(e, i)}
                          onDragEnd={handleTxtDragEnd}
                        >
                          {txts.length > 0 ? (
                            <span className="txt-slot__preview">{previewText || 'Text'}</span>
                          ) : (
                            <div className="txt-slot__empty"><Type size={10} /></div>
                          )}
                          {txts.length > 1 && <span className="txt-slot__count">{txts.length}</span>}
                          {txtSpan > 1 && <span className="txt-slot__span-badge">{txtSpan}×</span>}
                        </div>
                      )
                    })()}
                  </div>
                </React.Fragment>
              )
            })
          )}
        </div>

        {/* Actions (rechts) */}
        <div className="anim-bar__actions">
          {/* Animation switcher dropdown */}
          <div className="anim-bar__anim-switcher" ref={animDropdownRef}>
            <button
              className="anim-bar__anim-switcher-btn"
              onClick={() => setAnimDropdownOpen(!animDropdownOpen)}
              data-tip="Animation wechseln"
            >
              <span className="anim-bar__anim-switcher-name">
                {activeAnimationId
                  ? (animations.find(a => a.id === activeAnimationId)?.name || presentationName)
                  : presentationName}
              </span>
              <ChevronDown size={12} />
            </button>
            {animDropdownOpen && (
              <div className="anim-bar__anim-dropdown">
                {animations.length === 0 && (
                  <div className="anim-bar__anim-dropdown-empty">Keine Animationen</div>
                )}
                {animations.map(anim => (
                  <div
                    key={anim.id}
                    className={`anim-bar__anim-dropdown-item${anim.id === activeAnimationId ? ' anim-bar__anim-dropdown-item--active' : ''}`}
                    onClick={() => { loadAnimation(anim.id); setAnimDropdownOpen(false) }}
                  >
                    <div className="anim-bar__anim-dropdown-item-info">
                      <span className="anim-bar__anim-dropdown-item-name">{anim.name}</span>
                      <span className="anim-bar__anim-dropdown-item-count">
                        {Array.isArray(anim.slides) ? anim.slides.length : 0} Slides
                      </span>
                    </div>
                    <button
                      className="anim-bar__anim-dropdown-item-del"
                      onClick={(e) => { e.stopPropagation(); deleteAnimation(anim.id) }}
                      title="Loschen"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                <button
                  className="anim-bar__anim-dropdown-create"
                  onClick={() => {
                    const name = prompt('Name der neuen Animation:')
                    if (name?.trim()) createAnimation(name.trim())
                  }}
                >
                  <Plus size={12} /> Neue Animation
                </button>
              </div>
            )}
          </div>

          <span className="anim-bar__counter">{slides.length > 0 ? `${currentSlide + 1}/${slides.length}` : '0/0'}</span>
          <button className="anim-bar__btn" onClick={savePresentation} data-tip="Animation speichern">
            <Save size={16} />
          </button>
          <button className="anim-bar__btn" onClick={enterPresentationMode} data-tip="Präsentation">
            <Maximize2 size={16} />
          </button>
          <button className="anim-bar__btn" onClick={() => (window as any)._settingsPanel?.toggle()} data-tip="Einstellungen">
            <Settings size={16} />
          </button>
        </div>
      </div>

      {/* Hidden file input for overlay images */}
      <input ref={overlayFileRef} type="file" accept="image/*" multiple
        style={{ display: 'none' }} onChange={handleOverlayFiles} />
    </div>,
    document.body
  )

  // ── Kombiniertes Popover Portal (Übergang + Verweildauer) ──
  const renderPopoverPortal = () => {
    if (editingSlide === null || editingSlide < 0 || !popoverPos) return null

    const slide = slides[editingSlide]
    if (!slide) return null
    return createPortal(
      <div
        className="anim-bar__popover anim-bar__popover--portal"
        style={clampPopupPos(popoverPos, 'above', { w: 210, h: 320 })}
        onPointerDown={e => e.stopPropagation()}
      >
        {/* ── Sektion: Verweildauer ── */}
        <div className="anim-bar__popover-section-title">Verweildauer</div>
        <div className="anim-bar__popover-row">
          <label>Pause (s)</label>
          <input type="number" min={0} max={60} step={0.5} value={slide.hold}
            onChange={e => updateSlide(editingSlide, { hold: parseFloat(e.target.value) || 3 })} />
        </div>
        <div className="anim-bar__popover-row">
          <label>Turntable</label>
          <input type="checkbox" checked={slide.holdTurntable ?? false}
            onChange={e => updateSlide(editingSlide, { holdTurntable: e.target.checked })} />
        </div>
        {(slide.holdTurntable) && (
          <div className="anim-bar__popover-row">
            <label>RPM</label>
            <input type="number" min={0.5} max={30} step={0.5} value={slide.holdTurntableRpm ?? 4}
              onChange={e => updateSlide(editingSlide, { holdTurntableRpm: parseFloat(e.target.value) || 4 })} />
          </div>
        )}

        {/* ── Trennlinie ── */}
        <div className="anim-bar__popover-divider" />

        {/* ── Sektion: Übergang ── */}
        <div className="anim-bar__popover-section-title">Übergang</div>
        <div className="anim-bar__popover-row">
          <label>Typ</label>
          <select value={slide.transition} onChange={e => updateSlide(editingSlide, { transition: e.target.value as AnimSlide['transition'] })}>
            <option value="fly">Fly</option><option value="orbit">Orbit</option><option value="cut">Cut</option>
          </select>
        </div>
        <div className="anim-bar__popover-row">
          <label>Dauer (s)</label>
          <input type="number" min={0.1} max={30} step={0.1} value={slide.duration}
            onChange={e => updateSlide(editingSlide, { duration: parseFloat(e.target.value) || 2 })} />
        </div>
        {slide.transition === 'orbit' && (
          <>
            <div className="anim-bar__popover-row">
              <label>RPM</label>
              <input type="number" min={0} max={60} step={1} value={slide.orbitSpeed ?? 6}
                onChange={e => updateSlide(editingSlide, { orbitSpeed: parseFloat(e.target.value) || 6 })} />
            </div>
            <div className="anim-bar__popover-row">
              <label>End°</label>
              <input type="number" min={0} max={720} step={15}
                value={slide.orbitEndAngle != null ? Math.round(slide.orbitEndAngle * 180 / Math.PI) : ''}
                placeholder="∞"
                onChange={e => { const d = parseFloat(e.target.value); updateSlide(editingSlide, { orbitEndAngle: isNaN(d) ? null : d * Math.PI / 180 }) }} />
            </div>
          </>
        )}
        <div className="anim-bar__popover-row">
          <label>Interpolieren</label>
          <input type="checkbox" checked={slide.interpolateProperties}
            onChange={e => updateSlide(editingSlide, { interpolateProperties: e.target.checked })} />
        </div>

        {/* ── Footer: Aktionen ── */}
        <div className="anim-bar__popover-actions">
          <button className="anim-bar__popover-dup" onClick={() => { duplicateSlide(editingSlide); setEditingSlide(null); setPopoverPos(null) }}>
            <Copy size={11} /> Duplizieren
          </button>
          <button className="anim-bar__popover-delete" onClick={() => { removeSlide(editingSlide); setEditingSlide(null); setPopoverPos(null) }}>
            <Trash2 size={11} /> Entfernen
          </button>
        </div>
      </div>,
      document.body
    )
  }

  // ── Scene picker portal ──
  const renderScenePicker = () => {
    if (!showPicker || !pickerPos) return null
    return createPortal(
      <div
        className="anim-bar__scene-picker"
        style={clampPopupPos(pickerPos, 'above', { w: 220, h: Math.min(300, 32 + scenes.length * 40) })}
        onPointerDown={e => e.stopPropagation()}
      >
        <div className="anim-bar__scene-picker-title">Szene hinzufügen</div>
        {scenes.length === 0 ? (
          <div className="anim-bar__scene-picker-empty">Keine Szenen</div>
        ) : scenes.map(s => (
          <button key={s.id} className="anim-bar__scene-picker-item" onClick={() => addSceneSlide(s.id)}>
            {s.thumbnailSmall && <img src={s.thumbnailSmall} alt="" className="anim-bar__scene-picker-thumb" />}
            <span>{s.title}</span>
          </button>
        ))}
      </div>,
      document.body
    )
  }

  // ── Context menu portal ──
  const renderCtxMenu = () => {
    if (!ctxMenu) return null
    const slide = slides[ctxMenu.idx]
    if (!slide) return null
    return createPortal(
      <div
        className="anim-bar__ctx-menu"
        style={clampPopupPos({ x: ctxMenu.x, y: ctxMenu.y }, 'cursor', { w: 180, h: 180 })}
        onPointerDown={e => e.stopPropagation()}
      >
        <button className="anim-bar__ctx-item" onClick={() => {
          const bar = document.querySelector('.anim-bar__bottom')
          const barTop = bar ? bar.getBoundingClientRect().top : ctxMenu.y
          setPopoverPos({ x: ctxMenu.x, y: barTop })
          setEditingSlide(ctxMenu.idx)
          closeCtxMenu()
        }}>
          <Settings size={12} /> Slide Einstellungen
        </button>
        <button className="anim-bar__ctx-item" onClick={() => ctxUpdateScene(ctxMenu.idx)}>
          <RefreshCw size={12} /> Szene aktualisieren
        </button>
        <div className="anim-bar__ctx-sep" />
        <button className="anim-bar__ctx-item" onClick={() => { duplicateSlide(ctxMenu.idx); closeCtxMenu() }}>
          <Copy size={12} /> Duplizieren
        </button>
        {slides.length > 1 && (
          <button className="anim-bar__ctx-item anim-bar__ctx-item--danger" onClick={() => { removeSlide(ctxMenu.idx); closeCtxMenu() }}>
            <Trash2 size={12} /> Entfernen
          </button>
        )}
      </div>,
      document.body
    )
  }

  // ── Overlay-Bilder Popover ──
  const renderOverlayPopover = () => {
    if (overlayEditIdx === null || !overlayPopPos) return null
    const slide = slides[overlayEditIdx]
    if (!slide) return null
    const imgs = slide.overlayImages || []
    const eidx = overlayEditIdx

    return createPortal(
      <div
        className="ovl-popover--portal anim-bar__popover anim-bar__popover--portal"
        style={clampPopupPos(overlayPopPos, 'above', { w: 300, h: 450 })}
        onPointerDown={e => e.stopPropagation()}
      >
        <div className="anim-bar__popover-row">
          <label>Span</label>
          <select value={slide.overlaySpan || 1}
            onChange={e => updateSlide(eidx, { overlaySpan: parseInt(e.target.value) || 1 })}>
            <option value={1}>1 Slide</option>
            <option value={2}>2 Slides</option>
            <option value={3}>3 Slides</option>
          </select>
        </div>

        <div className="anim-bar__popover-row">
          <label>Übergang</label>
          <select value={slide.overlayTransition || 'cut'}
            onChange={e => updateSlide(eidx, { overlayTransition: e.target.value as 'fade' | 'cut' })}>
            <option value="cut">Cut</option>
            <option value="fade">Fade</option>
          </select>
        </div>

        <div className="imgstrip__popover-images-header">
          <span>Bilder ({imgs.length})</span>
          <button className="imgstrip__popover-upload-btn" onClick={() => triggerOverlayUpload(eidx)}>
            <Upload size={10} /> Hochladen
          </button>
        </div>

        {imgs.length === 0 ? (
          <div className="imgstrip__popover-empty">Keine Bilder</div>
        ) : (
          <div className="imgstrip__popover-images-list">
            {imgs.map(img => (
              <div key={img.id} className="imgstrip__popover-image-item">
                <img src={img.url} alt="" className="imgstrip__popover-image-thumb" />
                <div className="imgstrip__popover-image-controls">
                  <span className="imgstrip__popover-image-name">{img.fileName}</span>
                  <div className="imgstrip__popover-vec3">
                    <label>Pos</label>
                    <input type="number" step={0.1} value={img.position.x}
                      onChange={e => updateOverlayImage(eidx, img.id, { position: { ...img.position, x: parseFloat(e.target.value) || 0 } })} />
                    <input type="number" step={0.1} value={img.position.y}
                      onChange={e => updateOverlayImage(eidx, img.id, { position: { ...img.position, y: parseFloat(e.target.value) || 0 } })} />
                    <input type="number" step={0.1} value={img.position.z}
                      onChange={e => updateOverlayImage(eidx, img.id, { position: { ...img.position, z: parseFloat(e.target.value) || 0 } })} />
                    <button className="imgstrip__pick-btn" title="Position von Kamera übernehmen"
                      onClick={() => {
                        const d = getOverlayDefaults()
                        updateOverlayImage(eidx, img.id, { position: d.position, rotation: d.rotation, scale: d.scale })
                      }}>
                      <Move size={10} />
                    </button>
                  </div>
                  <div className="imgstrip__popover-vec3">
                    <label>rX</label>
                    <input type="number" step={5} value={img.rotation.x}
                      onChange={e => updateOverlayImage(eidx, img.id, { rotation: { ...img.rotation, x: parseFloat(e.target.value) || 0 } })} />
                  </div>
                  <div className="imgstrip__popover-vec3">
                    <label>rY</label>
                    <input type="number" step={5} value={img.rotation.y}
                      onChange={e => updateOverlayImage(eidx, img.id, { rotation: { ...img.rotation, y: parseFloat(e.target.value) || 0 } })} />
                  </div>
                  <div className="imgstrip__popover-vec3">
                    <label>rZ</label>
                    <input type="number" step={5} value={img.rotation.z}
                      onChange={e => updateOverlayImage(eidx, img.id, { rotation: { ...img.rotation, z: parseFloat(e.target.value) || 0 } })} />
                  </div>
                  <div className="imgstrip__popover-vec2">
                    <label>Größe</label>
                    <input type="number" step={0.1} min={0.1} value={img.scale.x}
                      onChange={e => updateOverlayImage(eidx, img.id, { scale: { ...img.scale, x: parseFloat(e.target.value) || 1 } })} />
                    <input type="number" step={0.1} min={0.1} value={img.scale.y}
                      onChange={e => updateOverlayImage(eidx, img.id, { scale: { ...img.scale, y: parseFloat(e.target.value) || 1 } })} />
                  </div>
                  <button className="imgstrip__popover-img-remove" onClick={() => removeOverlayImage(eidx, img.id)}>
                    <Trash2 size={10} /> Entfernen
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>,
      document.body
    )
  }

  // ── Text-Overlay Popover ──
  const renderTxtPopover = () => {
    if (txtEditIdx === null || !txtPopPos) return null
    const slide = slides[txtEditIdx]
    if (!slide) return null
    const txts = slide.textOverlays || []

    return createPortal(
      <div
        className="txt-popover--portal anim-bar__popover anim-bar__popover--portal"
        style={clampPopupPos(txtPopPos, 'above', { w: 280, h: 400 })}
        onPointerDown={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        onKeyUp={e => e.stopPropagation()}
      >
        {/* Span */}
        <div className="anim-bar__popover-row">
          <label>Span</label>
          <select value={slide.textSpan || 1}
            onChange={e => { if (txtEditIdx !== null) updateSlide(txtEditIdx, { textSpan: parseInt(e.target.value) || 1 }) }}>
            <option value={1}>1 Slide</option>
            <option value={2}>2 Slides</option>
            <option value={3}>3 Slides</option>
          </select>
        </div>

        {/* Transition */}
        <div className="anim-bar__popover-row">
          <label>Übergang</label>
          <select value={slide.textTransition || 'cut'}
            onChange={e => { if (txtEditIdx !== null) updateSlide(txtEditIdx, { textTransition: e.target.value as 'fade' | 'cut' }) }}>
            <option value="cut">Cut</option>
            <option value="fade">Fade</option>
          </select>
        </div>

        {/* Text entries */}
        <div className="imgstrip__popover-images-header">
          <span>Texte ({txts.length})</span>
          <button className="imgstrip__popover-upload-btn" onClick={() => { if (txtEditIdx !== null) addTextOverlay(txtEditIdx) }}>
            <Plus size={10} /> Hinzufügen
          </button>
        </div>

        {txts.length === 0 ? (
          <div className="imgstrip__popover-empty">Keine Texte</div>
        ) : (
          <div className="imgstrip__popover-images-list">
            {txts.map(txt => {
              const eidx = txtEditIdx!
              return (
                <div key={txt.id} className="txt-popover__item">
                  <textarea
                    className="txt-popover__textarea"
                    value={txt.text}
                    placeholder="Text eingeben…"
                    rows={2}
                    onChange={e => updateTextOverlay(eidx, txt.id, { text: e.target.value })}
                    onKeyDown={e => e.stopPropagation()}
                    onKeyUp={e => e.stopPropagation()}
                    onKeyPress={e => e.stopPropagation()}
                    onClick={e => e.stopPropagation()}
                    onFocus={e => e.stopPropagation()}
                  />
                  <div className="txt-popover__controls">
                    <div className="imgstrip__popover-vec2">
                      <label>Größe</label>
                      <input type="number" step={0.5} min={0.5} max={20} value={txt.fontSize}
                        onChange={e => updateTextOverlay(eidx, txt.id, { fontSize: parseFloat(e.target.value) || 3 })} />
                      <span style={{ fontSize: '9px', opacity: 0.5 }}>vw</span>
                    </div>
                    <div className="imgstrip__popover-vec2">
                      <label>Pos X</label>
                      <input type="number" step={1} min={0} max={100} value={txt.position.x}
                        onChange={e => updateTextOverlay(eidx, txt.id, { position: { ...txt.position, x: parseFloat(e.target.value) || 0 } })} />
                      <span style={{ fontSize: '9px', opacity: 0.5 }}>%</span>
                    </div>
                    <div className="imgstrip__popover-vec2">
                      <label>Pos Y</label>
                      <input type="number" step={1} min={0} max={100} value={txt.position.y}
                        onChange={e => updateTextOverlay(eidx, txt.id, { position: { ...txt.position, y: parseFloat(e.target.value) || 0 } })} />
                      <span style={{ fontSize: '9px', opacity: 0.5 }}>%</span>
                    </div>
                    <div className="imgstrip__popover-vec2 txt-popover__color-row">
                      <label>Farbe</label>
                      <input type="color" value={txt.color}
                        onChange={e => updateTextOverlay(eidx, txt.id, { color: e.target.value })} />
                      {['#ffffff', '#4242F5', '#000000'].map(c => (
                        <button key={c} className={`txt-popover__color-swatch${txt.color === c ? ' txt-popover__color-swatch--active' : ''}`}
                          style={{ background: c }}
                          onClick={() => updateTextOverlay(eidx, txt.id, { color: c })} />
                      ))}
                    </div>
                    <div className="imgstrip__popover-vec2">
                      <label>Opacity</label>
                      <input type="range" min={0} max={1} step={0.05} value={txt.opacity}
                        onChange={e => updateTextOverlay(eidx, txt.id, { opacity: parseFloat(e.target.value) })} />
                    </div>
                    <button className="imgstrip__popover-img-remove" onClick={() => removeTextOverlay(eidx, txt.id)}>
                      <Trash2 size={10} /> Entfernen
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>,
      document.body
    )
  }

  return (
    <>
      {mainBar}
      {renderScenePicker()}
      {renderPopoverPortal()}
      {renderCtxMenu()}
      {renderOverlayPopover()}
      {renderTxtPopover()}
    </>
  )
}
